import React, { useState } from 'react'
import { EditSaveCancelButtons, Panel, useIsEditing } from './blocks'
import { cn } from '~/lib/utils'
import { useProfileMutation } from './QueryClient'
import {
  HeartIcon,
  SparklesIcon,
  FilmIcon,
  XMarkIcon,
  BoltIcon,
  StarIcon,
  BookOpenIcon,
  TrophyIcon,
} from '@heroicons/react/24/outline'
import { MusicDoubleNote } from 'iconoir-react'
import { CoffeeIcon } from './CoffeeIcon'
import { BullseyeIcon } from './BullseyeIcon'
import { PetIcon } from './PetIcon'

const availableQuestions = [
  { question: 'Hobbies & Interests', icon: HeartIcon },
  { question: 'Coffee Order', icon: CoffeeIcon },
  { question: 'Favorite Film', icon: FilmIcon },
  { question: 'Weekend Vibes', icon: SparklesIcon },
  { question: 'Superpower', icon: BoltIcon },
  { question: 'Hidden Talent', icon: StarIcon },
  { question: 'Learning Right Now', icon: BookOpenIcon },
  { question: 'Bucket List', icon: BullseyeIcon },
  { question: 'Always Cheering For', icon: TrophyIcon },
  { question: 'Playlist at My Desk', icon: MusicDoubleNote },
  { question: 'Desk-Side Companion (pet)', icon: PetIcon },
]

const positionColor = [
  'border-link-water-800 bg-gradient-to-br from-link-water-50 to-link-water-200/20',
  'border-granny-apple-800 bg-gradient-to-br from-granny-apple-50 to-granny-apple-200/20',
  'border-beeswax-800 bg-gradient-to-br from-beeswax-50 to-beeswax-200/20',
  'border-blue-chalk-800 bg-gradient-to-br from-blue-chalk-50 to-blue-chalk-200/20',
  'border-intoo-blue-medium bg-gradient-to-br from-intoo-blue-medium-100 to-intoo-blue-medium-200/20',
]

const GetToKnowMe = ({ currentData }) => {
  const [updatedData, setUpdatedData] = useState(currentData || {})
  const profileMutation = useProfileMutation()
  const dataChanged =
    JSON.stringify(updatedData) !== JSON.stringify(currentData || {})

  const saveClick = () => {
    dataChanged
      ? profileMutation.mutate({ get_to_know_me: updatedData })
      : cancelClick()
  }

  const cancelClick = () => {
    setUpdatedData(currentData || {})
  }

  const selectQuestion = (question) => {
    if (Object.keys(updatedData).length >= 5) return

    setUpdatedData({ ...updatedData, [question]: '' })
  }

  const nothingSelected = Object.keys(updatedData).length === 0

  return (
    <Panel>
      <div className="mb-6 flex w-full flex-row justify-between">
        <div className="flex flex-col gap-y-2">
          <div className="text-xl font-medium tracking-wide text-intoo-text">
            Get to Know Me
          </div>
          <div>
            Choose up to 5 fun questions to share more about yourself
            <span className="ml-1 text-intoo-blue-medium">
              ({Object.keys(updatedData).length}/5 selected)
            </span>
          </div>
        </div>
        <EditSaveCancelButtons
          sectionName="Questions"
          saveClick={saveClick}
          cancelClick={cancelClick}
        />
      </div>
      <div className="grid grid-cols-4 gap-3">
        {availableQuestions.map(({ question, icon }) => {
          const isSelected = Object.keys(updatedData).includes(question)
          const props = { question, icon, isSelected, selectQuestion }
          return <QuestionCard key={question} {...props} />
        })}
      </div>
      <div className="flex flex-col gap-y-6">
        {nothingSelected ? (
          <BlankAnswersSection />
        ) : (
          <AnswerInputs
            updatedData={updatedData}
            setUpdatedData={setUpdatedData}
          />
        )}
      </div>
    </Panel>
  )
}

export default GetToKnowMe

const AnswerInputs = ({ updatedData, setUpdatedData }) => {
  const removeQuestion = (question) => {
    const newData = { ...updatedData }
    delete newData[question]
    setUpdatedData(newData)
  }

  const setAnswer = (question) => (answer) => {
    setUpdatedData({ ...updatedData, [question]: answer })
  }

  return Object.entries(updatedData).map((entry, index) => {
    const [question, answer] = entry
    return (
      <AnswerCard
        key={`${question}-${index}`}
        question={question}
        answer={answer || ''}
        index={index}
        removeQuestionClick={() => removeQuestion(question)}
        setAnswer={setAnswer(question)}
      />
    )
  })
}

const BlankAnswersSection = () => {
  const { isEditing } = useIsEditing()
  if (!isEditing) return null
  return (
    <div className="flex flex-col items-center gap-y-3 rounded-lg bg-[#ececf0]/20 py-12">
      <SparklesIcon className="block size-12 text-[#717182]" />
      <div className="text-intoo-text/70">
        Click on a question above to start sharing about yourself!
      </div>
    </div>
  )
}

const AnswerCard = ({
  question,
  answer,
  index,
  setAnswer,
  removeQuestionClick,
}) => {
  const defaultContainerClass =
    'flex flex-col gap-y-3 rounded-lg border-black/10 p-4.5 border-l-4'
  const { isEditing } = useIsEditing()
  const entry = availableQuestions.find((e) => e.question === question)
  const { icon: Icon } = entry || {}
  const inputClass =
    'w-full rounded-md bg-white py-1 px-3 text-intoo-text placeholder:text-[#565656] border-none'
  return (
    <div className={cn(defaultContainerClass, positionColor[index])}>
      <div className="flex flex-row items-center justify-between">
        <div className="font-medium text-intoo-text">
          {Icon && (
            <Icon className="mr-2 inline-block size-5 stroke-2 text-intoo-blue-medium" />
          )}
          {question}
        </div>
        {isEditing && (
          <button onClick={removeQuestionClick}>
            <XMarkIcon className="size-5 text-intoo-text/50 hover:text-intoo-text" />
          </button>
        )}
      </div>
      <input
        type="text"
        name={question}
        value={answer}
        disabled={!isEditing}
        onChange={(e) => setAnswer(e.target.value)}
        className={isEditing ? cn(inputClass, 'shadow') : inputClass}
        placeholder={`Share your ${question.toLowerCase()}...`}
      />
    </div>
  )
}

const QuestionCard = ({ question, icon: Icon, isSelected, selectQuestion }) => {
  const { isEditing } = useIsEditing()
  if (isSelected) {
    return null
  }
  const onClick = () => {
    selectQuestion(question)
  }

  return (
    <button
      onClick={onClick}
      disabled={!isEditing}
      className="flex flex-col items-center gap-y-2 rounded-lg border-2 border-black/10 bg-gradient-to-br from-white to-[#ececf0]/30 p-4.5"
    >
      <Icon className="size-6 stroke-2 text-intoo-blue-medium" />
      <div className="text-intoo-text">{question}</div>
    </button>
  )
}
